import React, { useState } from 'react';
import {
  IAuditoriaAdiantamentoFuncionariosResultado,
  IItemAdiantamentoFuncionarios,
} from '../interfaces/IAuditoriaAdiantamentoFuncionarios';
import { formatCurrency, formatDate } from '../utils/formatters';

// COR-276: drill-down a nível de lançamento — todo AVISO de estagnação precisa mostrar não só o
// saldo agregado, mas os lançamentos reais (débitos de entrada + créditos de baixa) que o formam.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemAdiantamentoFuncionarios }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

interface Props {
  resultado: IAuditoriaAdiantamentoFuncionariosResultado;
}



const getTipoBadge = (tipoMovimento?: string) => {
  if (tipoMovimento === 'EMPRESTIMO_FORMAL') {
    return <span className='badge badge-light-info ms-1'>Empréstimo</span>;
  }
  return <span className='badge badge-light-warning ms-1'>Adiantamento</span>;
};

const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

const AuditoriaAdiantamentoFuncionariosDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const adiantamentosEstagnados = resultado.adiantamentosEstagnados || [];
  const adiantamentosConciliados = resultado.adiantamentosConciliados || [];
  // COR-61: contas com divergência cadastral/referencial.
  const contasDivergenciaReferencial = resultado.contasDivergenciaReferencial || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Adiantamentos e Empréstimos a Funcionários — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Multa ECD potencial — 5% sobre o saldo estagnado, limitada a 1% da receita bruta do período (Art. 12, II, Lei 8.218/1991).</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>Adiantamentos/empréstimos a funcionários estagnados por mais de 30 dias no período.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base fiscal e contábil aplicada a esta auditoria.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
          <div className='mt-2'><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Adiantamentos/Empréstimos a Funcionários com Saldo Pendente (&gt; 30 dias)</h3>
        <p className='text-muted'>
          Contas auditadas com saldo devedor por mais de 30 dias, separadas em dois tratamentos:{' '}
          <strong>Adiantamento Operacional</strong> (salarial, viagem, férias — multa ECD potencial sobre o saldo estagnado)
          e <strong>Empréstimo Formal</strong> (mútuo, parcelado, desconto em folha — sem multa automática; exige
          análise contratual e verificação de parcelas).
        </p>

        {adiantamentosEstagnados.length === 0 && (
          <div className='text-muted mt-4'>Nenhum adiantamento/empréstimo a funcionário estagnado identificado no período.</div>
        )}

        {adiantamentosEstagnados.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Tipo</th>
                  <th>Último Débito</th>
                  <th className='text-center'>Dias Sem Baixa</th>
                  <th className='text-end'>Saldo</th>
                  <th className='text-end'>Impacto Fiscal</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {adiantamentosEstagnados.map((item: IItemAdiantamentoFuncionarios, idx) => {
                  const chave = `af-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;
                  const ehEmprestimo = item.tipoMovimento === 'EMPRESTIMO_FORMAL';

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{getTipoBadge(item.tipoMovimento)}</td>
                        <td>{formatDate(item.dataUltimoDebito)}</td>
                        <td className='text-center'>
                          <span className={`auditoria-status status-${item.status}`}>{item.diasSemBaixa} dias</span>
                        </td>
                        <td className='text-end'>{formatCurrency(item.saldoEstagnado)}</td>
                        <td className='text-end'>
                          {ehEmprestimo
                            ? <span className='text-muted' style={{ fontSize: 11 }}>Análise contratual</span>
                            : formatCurrency(item.impactoFiscalTotal)}
                        </td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              {!ehEmprestimo && <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>}
                              <div><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
                              <BlocoLancamentosRelacionados item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {contasDivergenciaReferencial.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-exclamation-triangle-fill text-warning me-2' style={{ fontSize: 18 }} />
            Contas com Referencial Possivelmente Divergente ({contasDivergenciaReferencial.length})
          </h3>
          <p className='text-muted'>
            Contas cadastradas com referencial RFB 1.01.02.01.03 (Adiantamentos a Funcionários) mas cuja
            nomenclatura indica outra natureza (fornecedor, cliente ou terceiro) — excluídas da auditoria
            de funcionários. Aviso de qualidade de ECD, sem impacto fiscal automático.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Referencial</th>
                  <th>Empresa</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contasDivergenciaReferencial.map((item: IItemAdiantamentoFuncionarios, idx) => {
                  const chave = `afd-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.contaContabil}</div>
                        </td>
                        <td>
                          <span className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</span>
                        </td>
                        <td>{item.empresaNome}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={4} style={{ background: '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div className='mt-1'><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {adiantamentosConciliados.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Adiantamentos/Empréstimos Verificados e Conciliados
          </h3>
          <p className='text-muted'>
            Contas de adiantamento/empréstimo a funcionários que foram auditadas e estão em conformidade — saldo
            zerado ou baixado dentro de 30 dias.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Tipo</th>
                  <th>Último Débito</th>
                  <th className='text-center'>Dias</th>
                  <th className='text-end'>Saldo</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {adiantamentosConciliados.map((item: IItemAdiantamentoFuncionarios, idx) => {
                  const chave = `afc-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{getTipoBadge(item.tipoMovimento)}</td>
                        <td>{item.dataUltimoDebito ? formatDate(item.dataUltimoDebito) : '-'}</td>
                        <td className='text-center'>{item.diasSemBaixa || '-'}</td>
                        <td className='text-end'>{formatCurrency(item.saldoEstagnado)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={6} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <i className='bi bi-check-circle-fill text-success me-1' />
                              <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaAdiantamentoFuncionariosDetalhe;
